import { z } from "zod";
import { BillingModel, Count, Money, NonNegativeMoney, ProviderType, Uuid } from "./common.ts";
import { actionsOf } from "./permissions.ts";

/*
 * The catalogue (07 §2.2 `GET /api/products`, 06 `product`, ADR-054). Texts and images come
 * from dataCycle over the gateway under `/dc`, keyed by `datacycleId`; this contract carries
 * what the platform owns: identity, billing model, prices, limits.
 */

/**
 * The product family from the classification tree "Produkttypen" in dataCycle (ADR-112): the
 * shelf of the catalogue. `id` is the concept, `name` its label; grouping goes by the id, the
 * label may change.
 */
export const ProductFamily = z.object({
  id: Uuid,
  name: z.string().min(1),
});
export type ProductFamily = z.infer<typeof ProductFamily>;

/**
 * How a product stands for the session's customer (ADR-158 point F12.2-11, AP-B14-07), the first
 * that holds: `booked` with an active or paused entitlement, `in_setup` as `inSetup`, `requested`
 * with an inquiry of the customer for it that is `new` or `forwarded`.
 */
export const ProductStanding = z.enum(["requested", "in_setup", "booked"]);
export type ProductStanding = z.infer<typeof ProductStanding>;

export const Product = z.object({
  id: Uuid,
  /** `@id` of the product in dataCycle, the link to its texts (ADR-054) */
  datacycleId: Uuid,
  /** copy of `dc:slug`, for addresses */
  slug: z.string().min(1),
  /** copy for staff views and activities; the display name comes from dataCycle */
  name: z.string().min(1),
  /** in the alpha release always the own company (R-14) */
  vendor: z.string().min(1),
  billingModel: BillingModel,
  /** how the product is set up (ADR-144 point 1); says nothing about keys */
  provider: ProviderType,
  /**
   * the product runs over the platform's AI keys (ADR-144): a key and a monthly limit come with
   * it, and the portal adds "zzgl. KI-Verbrauch nach Nutzung" to its price (point 9)
   */
  aiAccess: z.boolean(),
  /** display price in the PO's currency, probably EUR; the invoice outside is binding (ADR-063) */
  price: Money.nullable(),
  /** proposed monthly limit at purchase (F-46), USD */
  defaultMonthlyLimit: NonNegativeMoney,
  /** present when the product offers a demo (UC-06): its limit and duration (ADR-053) */
  demo: z
    .object({
      monthlyLimit: NonNegativeMoney,
      durationDays: Count.min(1),
    })
    .nullable(),
  /**
   * activated by the admin; the catalogue shows only a product that is published and active, one
   * on request too (ADR-146), so here it is always true
   */
  active: z.boolean(),
  /**
   * Only on request (ADR-112): the product stands in the catalogue without an order or a demo,
   * "Anfrage senden" instead. Set by the classification "Anfrage" in dataCycle, never by the
   * platform. `price` and `demo` are null for such a product; its terms are placeholders.
   */
  inquiryOnly: z.boolean(),
  /** the family of the product, null without one (ADR-112) */
  family: ProductFamily.nullable(),
  sortOrder: z.number().int(),
  /**
   * What the actor may do with this product (ADR-075): present with a session, absent for
   * guests. Reasons that occur: `role_required`, `customer_suspended`, `inquiry_only` (before
   * every other reason at the product), `demo_only`, `entitlement_exists`, `order_open`,
   * `demo_already_used`, `demo_not_offered`.
   */
  actions: actionsOf("order.place", "demo.request").optional(),
  /**
   * The product is being set up for the session's customer (ADR-150 addendum of 2026-10-30, PM6):
   * an open order of it (received, in progress) or a pending entitlement. Present with a session
   * of a customer, absent for guests and for staff without one. The portal shows "In Einrichtung"
   * from it, also to a user who may not read the orders.
   */
  inSetup: z.boolean().optional(),
  /**
   * How the product stands for the session's customer (`ProductStanding`, ADR-158 point
   * F12.2-11): tiles and stages show "Gebucht", "In Einrichtung" or "Angefragt" from it, for every
   * role of the customer. Null without one, and always for guests and the team. The API sends it
   * in every answer; optional like `inSetup`, so a product without it reads as null.
   */
  standing: ProductStanding.nullable().optional(),
});
export type Product = z.infer<typeof Product>;

/** `GET /api/products`. Not paged: the catalogue is one product in the alpha release and a handful later. */
export const ProductListResponse = z.object({
  items: z.array(Product),
});
export type ProductListResponse = z.infer<typeof ProductListResponse>;
